import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import SeeItLive from './SeeItLive';

const FAQS = [
  {
    q: 'Is a non-dividend portfolio riskier than a dividend portfolio?',
    a: 'Structurally, yes. Non-dividend stocks are priced primarily on future growth, which means they carry more discount-rate risk and typically deeper drawdowns in bear markets. In 2022 the marquee non-dividend names drew down 55-84% peak-to-trough (META -76%, NFLX -76%, PLTR -84%, TSLA -74%, NVDA -66%, AMZN -56%). The tradeoff is that the recovery-plus-growth over the following two years produced returns that decisively beat dividend indices. Non-dividend portfolios reward patience and tolerate volatility, and punish over-sizing. The construction rules in this guide are designed to make the drawdowns survivable.',
  },
  {
    q: 'What percentage of my portfolio should be in non-dividend stocks?',
    a: 'A defensible framework: your non-dividend allocation should not exceed your genuine drawdown tolerance times two. If you can emotionally sustain a 30% total-portfolio drawdown without panic-selling, and you assume the non-dividend basket could draw down 60% in a bad year (a reasonable historical assumption), then non-dividend stocks should not exceed 50% of the portfolio. Younger investors with 20+ years to compound can safely run higher allocations because the recovery cycle has time to play out. Older investors approaching retirement should meaningfully lower non-dividend exposure regardless of conviction, because sequence-of-returns risk is now a real headwind against high-drawdown assets.',
  },
  {
    q: 'How many non-dividend positions should I hold?',
    a: 'Between 8 and 20 in most retail portfolios. Below 8, you are running concentrated risk — a single blow-up wipes out a meaningful percentage of the entire non-dividend book. Above 20, you have not enough conviction in each individual name to justify separate ownership rather than an ETF. Institutional non-div funds typically run 25-50 positions but they have research teams that can keep active track of that many; retail investors realistically cannot. If you find yourself owning 30 non-dividend positions, either consolidate to your highest-conviction 15 or move a portion to a broad growth ETF (QQQ, MGK, VUG) to let the diversification happen on autopilot.',
  },
  {
    q: 'How should I split between core compounders and speculative bets?',
    a: 'A useful shortcut: 70/20/10. Roughly 70% of the non-dividend allocation in high-quality core compounders (BUY verdict on the Capital Reinvestment Score, Altman Safe Zone, Beneish clean, positive Operating Momentum). Roughly 20% in growth-with-flags names (WATCH verdict, one signal amber, high-quality thesis). Roughly 10% in speculative pre-revenue or turnaround stories where you accept a full write-down probability of 30-50%. The exact split flexes with age and drawdown tolerance, but the shape holds: most of the money is in businesses that will still exist and compound in a decade, small amounts in bets that could double or die.',
  },
  {
    q: 'How do I use the Capital Reinvestment Score to pick core positions?',
    a: 'The Capital Reinvestment Score ranks every non-dividend name in the universe on a 1-100 scale using five weighted pillars (Free Cash Flow, Balance Sheet, Efficiency, Growth, Forensic Safety). A rank of 85+ with a BUY verdict pill puts the ticker in the top ~15% of the non-dividend universe. That is where core compounders live. Uber currently reads 96.1, Palantir reads 96.7, Nvidia reads high enough to be actively BUY-rated. WATCH-rated names in the 60-85 band are for satellite positions where you accept more thesis risk in exchange for a possibly higher entry-point return. AVOID-rated names (below ~40) should not be in the portfolio at all unless you have a very specific catalyst thesis and a small position size to match.',
  },
  {
    q: 'What is the biggest portfolio-construction mistake retail investors make with non-dividend stocks?',
    a: 'Owning too many correlated positions. Every retail investor who owned Meta, Netflix, Nvidia, Tesla, and Amazon in early 2022 discovered in real time that these five names moved together during the growth-stock re-rating. A "diversified" portfolio of five megacap growth names is really one concentrated bet on the megacap-growth factor. Real diversification within the non-dividend universe means owning across categories: platform megacaps, high-Rule-of-40 SaaS, capital-intensive growth (RIVN, Tesla-earlier-years), specialty pharma, and one or two idiosyncratic single-story names. The correlation matrix is what saves the portfolio during a factor drawdown — not the ticker count.',
  },
];

export default function LearnPortfolioConstructionNonDiv() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Building a Non-Dividend Portfolio — Allocation, Sizing, Correlation | TwylightCrow"
      description="How to structure a portfolio of non-dividend stocks: allocation ceilings, position sizing to drawdown tolerance, the 70/20/10 core-satellite framework, and how to use the Capital Reinvestment Score to filter core compounders. Live-verified examples with Uber (rank 96), Palantir (rank 97), Peloton (rank 65 WATCH), Lucid (rank 29 AVOID)."
      canonical="https://twylightcrow.com/learn/portfolio-construction-non-div"
      guidePath="/learn/portfolio-construction-non-div"
      sectionLabel="Capital Allocation &amp; Tax"
      sectionAnchor="non-div-capital-allocation"
      kicker="Learn · Non-Dividend Stocks"
      h1="Building a Non-Dividend Portfolio — Allocation, Sizing, Correlation"
      datePublished="2026-07-04"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/volatility-survival-non-div', label: 'Volatility Survival — the drawdown math this guide sizes against' },
        { to: '/learn/hidden-gems-vs-value-traps', label: 'Hidden Gems vs Value Traps — the forensic gate for every candidate' },
        { to: '/learn/valuation-non-dividend-stocks', label: 'Valuing Non-Dividend Stocks — the multiples check on entry' },
        { to: '/learn/rule-of-40', label: 'Rule of 40 — the efficiency lens for core-vs-satellite classification' },
        { to: '/learn/debt-to-growth-ratio', label: 'Debt-to-Growth Ratio — leverage check for the satellite bucket' },
        { to: '/glossary#entry-capital-reinvestment-score', label: 'Glossary: Capital Reinvestment Score' },
      ]}
    >
      <p>
        Twenty-two guides in, you have the individual lenses.
        Valuation multiples. Rule of 40. Free Cash Flow Growth.
        Debt-to-Growth. Volatility Survival. Hidden Gems vs Value
        Traps. Institutional 13-F Tracking. Each one answers a
        specific question about a specific stock. This guide is
        different. It answers the meta-question that all the
        others feed into: <em>how do you turn 22 lenses into an
        actual portfolio?</em>
      </p>
      <p>
        The framework in this guide is deliberately conservative
        because non-dividend investing rewards patience and
        punishes over-sizing. Get the shape right and the
        individual stock picks matter far less than the standard
        retail wisdom suggests.
      </p>

      <h2>Step 1 &mdash; The total allocation ceiling</h2>
      <p>
        Before picking a single ticker, decide how much of your
        total portfolio can defensibly sit in the non-dividend
        universe. The math is straightforward:
      </p>
      <p className="pl-4 border-l-2 border-violet-500/50 font-mono text-sm">
        Max non-div allocation = your drawdown tolerance % &divide; 2
      </p>
      <p>
        If a 30% total-portfolio drawdown is the emotional pain
        threshold you can genuinely sit through without
        capitulating, the non-dividend basket ceiling is 60% of
        the portfolio &mdash; because that basket could realistically
        draw down 50-60% in a bad macro year. Younger investors
        (20+ year horizon) can flex this upward; investors within
        5 years of retirement should flex it downward because
        sequence-of-returns risk is now working against them.
      </p>

      <h2>Step 2 &mdash; The 70 / 20 / 10 core-satellite split</h2>
      <p>
        Within the non-dividend allocation, split into three
        buckets by conviction and thesis risk:
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Bucket</th>
            <th>Share</th>
            <th>Definition</th>
            <th>DiviDrip signal</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">Core compounders</td>
            <td className="font-mono">~70%</td>
            <td>High-quality businesses that should still exist and compound in a decade.</td>
            <td>Capital Reinvestment Score 85+, BUY verdict, Altman Safe Zone, clean Beneish, positive Operating Momentum.</td>
          </tr>
          <tr>
            <td className="font-semibold">Satellites</td>
            <td className="font-mono">~20%</td>
            <td>Growth-with-flags. Real business, real thesis, one or two signals amber.</td>
            <td>Score 60-85, WATCH verdict, one dimension amber but composite intact.</td>
          </tr>
          <tr>
            <td className="font-semibold">Speculative bets</td>
            <td className="font-mono">~10%</td>
            <td>Pre-revenue biotech, turnaround stories, high-conviction contrarian bets. Accept 30-50% write-down probability.</td>
            <td>Score varies. Use the Pre-Revenue framework for cash-runway names.</td>
          </tr>
        </tbody>
      </table>

      <h2>Step 3 &mdash; Position count discipline</h2>
      <p>
        Between 8 and 20 individual non-dividend positions works
        for most retail portfolios. The floor of 8 keeps you
        diversified enough that a single blow-up does not sink
        the ship. The ceiling of 20 acknowledges that you
        realistically cannot actively track more than that
        number of individual theses at retail scale. Beyond 20,
        move the marginal exposure into a broad growth ETF (QQQ,
        VUG, MGK) where the diversification happens automatically
        and you stop pretending to have conviction on names you
        do not actually follow.
      </p>

      <h2>Step 4 &mdash; Per-position sizing</h2>
      <p>
        The critical rule: size each position such that a 60%
        drawdown on that position is emotionally survivable
        without panic-selling. The math:
      </p>
      <p className="pl-4 border-l-2 border-violet-500/50 font-mono text-sm">
        Max position size = per-position pain threshold &divide; 60%
      </p>
      <p>
        A retail investor with $250,000 in the portfolio who can
        emotionally tolerate a $10,000 loss on a single position
        can size any single non-dividend name up to roughly
        $16,700 &mdash; about 6.7% of the total portfolio at
        entry. Core compounders can push toward that ceiling;
        satellites should sit noticeably below; speculative bets
        should be sized so a full write-down is not portfolio-
        threatening. See the{' '}
        <Link to="/learn/volatility-survival-non-div">
          Volatility Survival guide
        </Link>{' '}
        for the full drawdown-tolerance framework.
      </p>

      <h2>Step 5 &mdash; Correlation, not ticker count</h2>
      <p>
        The most common retail construction mistake is confusing
        ticker count with diversification. Owning Meta, Netflix,
        Amazon, Nvidia, and Tesla is five tickers but effectively
        one factor bet on megacap growth. Real non-dividend
        diversification comes from mixing categories:
      </p>
      <ul>
        <li>
          <strong>Platform megacaps</strong> (AMZN, GOOGL, META, MSFT if you exclude the dividend): mature moats, cash-flow machines.
        </li>
        <li>
          <strong>Elite Rule-of-40 SaaS</strong> (PLTR, CRWD, ADBE non-div-adjacent): efficiency + growth composite.
        </li>
        <li>
          <strong>Capital-intensive growth</strong> (RIVN, TSLA, LCID early): capex-heavy ramps where debt-to-growth is the primary risk.
        </li>
        <li>
          <strong>Consumer / delivery platforms</strong> (UBER, DASH): unit-economics-turning stories.
        </li>
        <li>
          <strong>AI / semiconductor cycle</strong> (NVDA, AVGO): concentrated exposure to a single capex cycle.
        </li>
        <li>
          <strong>Specialty pharma / clinical biotech</strong> (VKTX, BEAM, CRSP): binary outcomes with cash-runway protection.
        </li>
      </ul>
      <p>
        A five-position portfolio drawn from five different
        categories is meaningfully more diversified than a
        ten-position portfolio drawn from a single category. The
        correlation matrix is what saves the portfolio during a
        factor drawdown.
      </p>

      <h2>A worked example</h2>
      <p>
        Imagine a $250,000 portfolio for a 35-year-old investor
        with a 30% drawdown tolerance and a 25-year compounding
        horizon. The construction sequence:
      </p>
      <ol>
        <li>
          <strong>Total non-div ceiling.</strong> 30% pain
          tolerance &times; 2 = 60% ceiling &rarr; up to $150,000
          in non-dividend stocks.
        </li>
        <li>
          <strong>Split.</strong> 70/20/10 &rarr; $105K core,
          $30K satellites, $15K speculative.
        </li>
        <li>
          <strong>Position sizing.</strong> $10K per-position
          pain tolerance &divide; 60% drawdown assumption =
          $16.7K position cap. So core positions run $10-16K
          each ($105K &div; ~8 core = ~$13K average). Satellites
          run smaller ($4-7K each). Speculatives run smallest
          ($1-3K each).
        </li>
        <li>
          <strong>Category distribution.</strong> Aim for at
          least one position each in platform megacap,
          Rule-of-40 SaaS, capital-intensive growth, consumer
          platform, and (optionally) semiconductor cycle. Reject
          any candidate that would push a category above 25% of
          the non-dividend allocation.
        </li>
        <li>
          <strong>Filter using Capital Reinvestment Score.</strong>{' '}
          For each candidate ticker, verify the score is in the
          right band for the intended bucket. A &ldquo;core&rdquo;
          candidate that reads WATCH is either downgraded to the
          satellite bucket or dropped entirely.
        </li>
      </ol>

      <h3>Example core positions using live scores</h3>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Ticker</th>
            <th>Score</th>
            <th>Verdict</th>
            <th>Category</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>UBER</span>
                <SeeItLive ticker="UBER" tab="capital-analytics" tone="emerald" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-emerald-600">96.1</td>
            <td className="font-semibold text-emerald-600">BUY</td>
            <td>Consumer platform / unit-economics turnaround</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>PLTR</span>
                <SeeItLive ticker="PLTR" tab="capital-analytics" tone="emerald" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-emerald-600">96.7</td>
            <td className="font-semibold text-emerald-600">BUY</td>
            <td>Elite Rule-of-40 SaaS &mdash; valuation risk applies but capital quality is elite</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>SMCI</span>
                <SeeItLive ticker="SMCI" tab="capital-analytics" tone="emerald" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-emerald-600">86.3</td>
            <td className="font-semibold text-emerald-600">BUY</td>
            <td>Semiconductor-adjacent AI-infrastructure play &mdash; forensic picture recovered post-2024</td>
          </tr>
        </tbody>
      </table>

      <h3>Example satellite candidates</h3>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Ticker</th>
            <th>Score</th>
            <th>Verdict</th>
            <th>Thesis</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>PTON</span>
                <SeeItLive ticker="PTON" tab="capital-analytics" tone="amber" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-amber-600">65.7</td>
            <td className="font-semibold text-amber-600">WATCH</td>
            <td>Turnaround thesis. Altman deep Distress means position sizing must reflect the balance-sheet risk.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>CVNA</span>
                <SeeItLive ticker="CVNA" tab="capital-analytics" tone="amber" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-amber-600">56.8</td>
            <td className="font-semibold text-amber-600">WATCH</td>
            <td>Recovery-plus-Beneish-flag. Beneish flag is the specific risk, not the leverage.</td>
          </tr>
        </tbody>
      </table>

      <h3>Examples of positions that should not be in the portfolio</h3>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Ticker</th>
            <th>Score</th>
            <th>Verdict</th>
            <th>Why avoid</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>LCID</span>
                <SeeItLive ticker="LCID" tab="capital-analytics" tone="rose" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-rose-600">29.0</td>
            <td className="font-semibold text-rose-600">AVOID</td>
            <td>Deep Distress Altman + negative equity. Ramp economics not confirmed. Speculative bucket if at all.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>SANA</span>
                <SeeItLive ticker="SANA" tab="capital-analytics" tone="rose" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-rose-600">23.0</td>
            <td className="font-semibold text-rose-600">AVOID</td>
            <td>Pre-revenue biotech in balance-sheet Distress. Even if the science works, dilution is guaranteed.</td>
          </tr>
        </tbody>
      </table>

      <h2>Rebalancing discipline</h2>
      <p>
        A non-dividend portfolio drifts far faster than a
        dividend portfolio because the individual positions
        move so much more. A position that starts at 6% of
        portfolio can easily become 12% after a 100% return
        (which is not uncommon in this universe). At that
        point, the position is running larger than the sizing
        framework called for and needs to be trimmed. Three
        rebalance rules that work in practice:
      </p>
      <ol>
        <li>
          <strong>Position-cap trim.</strong> When any single
          position exceeds 1.5x its intended sizing, trim back
          to 1x. Sell the excess into a broad ETF or into
          rebalancing the underweight positions.
        </li>
        <li>
          <strong>Verdict downgrade trim.</strong> Any
          previously-BUY-rated position that migrates to WATCH
          gets downgraded to the satellite bucket regardless of
          entry conviction. Any WATCH-to-AVOID migration is a
          full exit.
        </li>
        <li>
          <strong>Correlation trim.</strong> Recompute the
          category distribution quarterly. Any category
          exceeding 25% of the non-dividend allocation gets
          trimmed. This is how you enforce diversification
          against factor drift &mdash; it does not happen
          automatically.
        </li>
      </ol>

      <h2>Common pitfalls</h2>
      <ul>
        <li>
          <strong>The concentration trap.</strong> Loading up on
          five megacap names because they feel safest, then
          discovering during a factor drawdown that &ldquo;safest&rdquo;
          did not mean uncorrelated.
        </li>
        <li>
          <strong>The story trap.</strong> Owning a AVOID-rated
          name because the story is compelling. The composite
          score exists specifically to override narrative
          conviction when the numbers disagree.
        </li>
        <li>
          <strong>The averaging-down trap.</strong> Doubling
          down on positions that have moved against you without
          first checking whether the forensic-safety picture
          has changed. See the{' '}
          <Link to="/learn/hidden-gems-vs-value-traps">
            Hidden Gems vs Value Traps guide
          </Link>{' '}
          for the check.
        </li>
        <li>
          <strong>The over-diversification trap.</strong>{' '}
          Owning 30+ individual positions and mistaking
          scattered exposure for research-backed conviction.
          Consolidate or move to an ETF.
        </li>
      </ul>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }, i) => (
          <div key={i} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open the{' '}
        <Link to="/dashboard">Dashboard</Link> and open the
        Capital Analytics tab on 5-10 non-dividend names you
        currently own or are considering. Note the score and
        verdict for each. Sort them mentally into the three
        buckets (core / satellite / speculative). Compare the
        actual allocation you already run against the 70/20/10
        target. Most retail non-dividend portfolios end up
        heavier in satellites than they should be &mdash;
        because satellite theses feel more exciting than
        boring compounders. The rebalance is usually the
        highest-value adjustment you can make in a single
        session.
      </p>

      <p className="mt-6">
        For the underlying signals this construction framework
        uses, see the{' '}
        <Link to="/learn/hidden-gems-vs-value-traps">
          Hidden Gems vs Value Traps
        </Link>{' '}
        and{' '}
        <Link to="/learn/volatility-survival-non-div">
          Volatility Survival
        </Link>{' '}
        guides.
      </p>
      <p className="text-xs text-muted-foreground italic">
        This guide is educational. Portfolio construction rules
        are frameworks, not personalised advice. Real portfolios
        should account for tax situation, retirement horizon,
        liquidity needs, and existing holdings that this guide
        does not address.
      </p>
    </LearnPage>
  );
}
